import { Component, OnInit, ChangeDetectorRef, Input } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators, FormArray } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import {
  BilletSpecieTypeEnum,
  BilletProtestTypeEnum,
  BilletDiscountRequestDto,
  BilletDiscountDetailsRequestDto,
  BilletDiscountTypeEnum,
  BilletProtestRequestDto,
  BilletFineRequestDto,
  BilletTrafficTicketTypeEnum,
  BilletInterestTypeEnum,
  BilletFeeRequestDto,
} from '@wlp/ui-bs-cashin';
import * as moment from 'moment';
import { LayoutConfigModel } from 'src/app/core/layout/models/layout-config.model';
import { LayoutConfigService } from 'src/app/core/layout/services/layout-config.service';
import { UiBsTranslateService } from '@wlp/ui-bs-translate';
@Component({
  selector: 'ui-t-form-emit-billet-details',
  templateUrl: './form-emit-billet-details.html',
  styleUrls: ['./form-emit-billet-details.scss'],
})
export class FormEmitBilletDetails implements OnInit {
  private layoutConfigValue: LayoutConfigModel;
  private langDefault: string;

  specieTypeOptions = Object.keys(BilletSpecieTypeEnum);
  specieTypeSelected: BilletSpecieTypeEnum = null;

  addProtest: boolean = false;
  proptestOptions = Object.keys(BilletProtestTypeEnum).map((protest) => ({
    key: protest,
    label: `CASH_OUT.BILLET_DETAILED_PAGE.PROTEST.SELECT_OPTIONS.${protest}`,
  }));
  protestSelected: BilletProtestTypeEnum = null;

  billetLotForm: FormGroup;
  discountForm: FormGroup;
  interestAndTrafficTicket: boolean = false;
  hasEnoughDiscounts: boolean = false;
  hasEnoughBillets: boolean = false;
  hasDiscount: boolean = false;
  descriptionCounter = 0;
  @Input() payerData?: { name: string; document: string };

  constructor(
    private formBuilder: FormBuilder,
    private layoutConfigService: LayoutConfigService,
    public modalController: ModalController,
    private translate: UiBsTranslateService,
    private cd: ChangeDetectorRef
  ) {
    moment.locale('pt-br');
  }

  ngOnInit() {
    this.layoutConfigValue = this.layoutConfigService.getSavedConfig();
    this.langDefault = this.layoutConfigValue.wlTheme.languageDefault;
    this.translate.setDefaultLang(this.langDefault);
    this.createLotForm();
  }

  createLotForm() {
    this.billetLotForm = this.formBuilder.group({
      specieType: new FormControl('', Validators.compose([Validators.required])),
      protest: new FormControl('', Validators.compose([Validators.required])),
      protestType: new FormControl('', Validators.compose([Validators.required])),
      protestDays: new FormControl(
        '',
        Validators.compose([Validators.required, Validators.min(1), Validators.max(99)])
      ),
      billetInterestType: new FormControl('', Validators.compose([Validators.required])),
      billetInterestValue: new FormControl('', Validators.compose([Validators.required])),
      billetInterestDays: new FormControl('', Validators.compose([Validators.required])),
      billetTrafficTicketType: new FormControl('', Validators.compose([Validators.required])),
      billetTrafficTicketValue: new FormControl('', Validators.compose([Validators.required])),
      billetTrafficTicketDays: new FormControl('', Validators.compose([Validators.required])),
      requestDescription: new FormControl('', Validators.compose([Validators.required])),
      discounts: this.formBuilder.array([this.createDiscountForm()]),
    });
  }

  get fineValue() {
    const ticketType = this.billetLotForm.value.billetTrafficTicketType;

    return {
      isBlocked: ticketType === 'FREE_TICKET' || ticketType === '',

      isMoney: ticketType === 'FIXED_TICKET',

      isPercentual: ticketType === 'PROPORTIONAL_TICKET',

      isNumber: ticketType === 'FREE_TICKET' || ticketType === '',

      maxLength: ticketType === 'FIXED_TICKET' ? '9' : ticketType === 'PROPORTIONAL_TICKET' ? '3' : '',
    };
  }

  protestBlockedField() {
    const protestType = this.billetLotForm.controls.protestType.value;
    return (
      protestType === 'NOT_PROTEST' ||
      protestType === 'CANCEL_PROTEST' ||
      protestType === null
    );
  }

  createDiscountForm(): FormGroup {
    this.discountForm = this.formBuilder.group({
      billetDiscountType: new FormControl('', Validators.compose([Validators.required])),
      billetDiscountValue: new FormControl('', Validators.compose([Validators.required])),
      billetDiscountDays: new FormControl('', Validators.compose([Validators.required])),
    });
    return this.discountForm;
  }

  get discounts(): FormArray {
    return this.billetLotForm.get('discounts') as FormArray;
  }

  handleDescriptionChange(description) {
    this.descriptionCounter = description.length;
  }

  addDiscount(): void {
    if (this.discounts.controls.length < 3) {
      this.discounts.push(this.createDiscountForm());
      if (this.discounts.controls.length === 3) {
        this.hasEnoughDiscounts = true;
      }
    }
    this.cd.detectChanges();
  }

  removeDiscount(index: number): void {
    this.discounts.removeAt(index);
    if (this.discounts.controls.length < 3) {
      this.hasEnoughDiscounts = false;
    }
  }

  getFees(): BilletFeeRequestDto {
    const feesInputToEnum = (value: string): BilletInterestTypeEnum => {
      switch (value) {
        case 'DAYS_INTEREST':
          return BilletInterestTypeEnum.DAYS_INTEREST;
        case 'PROPORTIONAL_INTEREST':
          return BilletInterestTypeEnum.PROPORTIONAL_INTEREST;
      }
      return null;
    };

    const feesType = this.billetLotForm.controls.billetInterestType.value;
    const feesValue = this.billetLotForm.controls.billetInterestValue.value;
    const feesDays = (this.billetLotForm.controls.billetInterestDays.value).trim().split('/').reverse().join('-');
    const selectedFees = feesInputToEnum(feesType);
    if (selectedFees == null) return null;
    return {
      type: selectedFees,
      value: feesValue,
      expirationDate: feesDays,
    };
  }

  getFine(): BilletFineRequestDto {
    const fineInputToEnum = (value: string): BilletTrafficTicketTypeEnum => {
      switch (value) {
        case 'FREE_TICKET':
          return BilletTrafficTicketTypeEnum.FREE_TICKET;
        case 'FIXED_TICKET':
          return BilletTrafficTicketTypeEnum.FIXED_TICKET;
        case 'PROPORTIONAL_TICKET':
          return BilletTrafficTicketTypeEnum.PROPORTIONAL_TICKET;
      }
      return null;
    };

    const fineType = this.billetLotForm.controls.billetTrafficTicketType.value;
    const finevalue = this.billetLotForm.controls.billetTrafficTicketValue.value;
    const fineDays = (this.billetLotForm.controls.billetTrafficTicketDays.value).trim().split('/').reverse().join('-');
    const selectedFine = fineInputToEnum(fineType);
    if (selectedFine == null) return null;

    return {
      type: selectedFine,
      value: finevalue,
      expirationDate: fineDays,
    };
  }

  getDiscounts(): BilletDiscountRequestDto {
    const discountCount = this.billetLotForm.controls.discounts['controls'].length;
    const discountDetails = [];
    for (let i = 0; i < discountCount; i++) {
      const details: BilletDiscountDetailsRequestDto = {
        value: this.billetLotForm.controls.discounts['controls'][i].controls.billetDiscountValue.value,
        expirationDate: (this.billetLotForm.controls.discounts['controls'][i].controls.billetDiscountDays.value).trim().split('/').reverse().join('-')
      };
      discountDetails.push(details);
    }

    const discountInputToEnum = (value: string): BilletDiscountTypeEnum => {
      switch (value) {
        case '0':
          return BilletDiscountTypeEnum.NOT_DISCOUNT;
        case '1':
          return BilletDiscountTypeEnum.FIXED_DISCOUNT;
        case '2':
          return BilletDiscountTypeEnum.PROPORTIONAL_DISCOUNT;
        case '3':
          return BilletDiscountTypeEnum.FIXED_IN_ADVANCE_DISCOUNT;
        case '4':
          return BilletDiscountTypeEnum.PROPORTIONAL_IN_ADVANCE_DISCOUNT;
      }
      return null;
    };

    const selectedDiscount = discountInputToEnum(
      this.billetLotForm.controls.discounts['controls'][0].controls.billetDiscountType.value
    );
    if (selectedDiscount == null) return null;

    return {
      type: selectedDiscount,
      details: discountDetails,
    };
  }

  getProtest(): BilletProtestRequestDto {
    const protestType = this.billetLotForm.controls.protestType.value;
    const protestDays = this.billetLotForm.controls.protestDays.value;
    if (!protestType) return null;

    return {
      type: protestType,
      days: protestDays,
    };
  }
}
